/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { debounce, TextField, TextFieldProps } from '@mui/material';
import { SnackbarAlert } from '../snackbarAlert/SnackbarAlert';
import { isAllowedKeyForNumberInput } from './numberInputFilter';
import { ApplicationConfig } from '../../../config/application.config';

const DEBOUNCE_TIME = 500;

export type DebouncedTextFieldProps = {
  value?: string;
  onDebounceChange?: (value: string) => void;
  // onChange is prohibited, since we can not guarantee that it is called when invalid
  onChange?: undefined;
  // Optionally, use this debounce time instead of the default (500ms). Should be constant.
  debounceTime?: number;
  // is called after onBlur, as the last method in the cycle.
  onUnfocus?: (currentValue?: string) => void;
} & TextFieldProps;

/**
 * Textfield with debounce functionality.
 */
export default function DebouncedTextField(props: DebouncedTextFieldProps) {
  const { onDebounceChange, value, debounceTime, onFocus, onBlur, InputLabelProps, onUnfocus, ...textFieldProps } =
    props;
  const [lastValue, setLastValue] = useState<string | undefined>(value);
  const [focused, setFocused] = useState<boolean>(false);
  const [valueHasChanged, setValueHasChanged] = useState<boolean>(false);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isFirefox = globalThis.navigator.userAgent.toLowerCase().includes('firefox');

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setValueHasChanged(true);
    setLastValue(event.target.value);
    if (event.target.validity.valid) {
      onDebounce(event.target.value);
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    setValueHasChanged(false);
    onFocus?.(event);
  };

  // We need to set the value in onBlur to prevent resetting to the old value. See COR-253
  const handleBlur = (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>): string | undefined => {
    setFocused(false);
    let currentValue = lastValue;
    if (valueHasChanged) {
      if (event.target.validity.valid) {
        currentValue = event.target.value;
        setLastValue(currentValue);
        // We need to call onDebounceChange here directly and not onDebounce, this could lead to the value resetting after onBlur otherwise
        onDebounceChange?.(currentValue);
      } else {
        setErrorMessage(event.target.validationMessage + ' Die Eingabe wurde gelöscht.');
        setShowError(true);
        const deletionValue = '';
        setLastValue(deletionValue);
        currentValue = deletionValue;
        onDebounceChange?.(deletionValue);
      }
    }
    onBlur?.(event);
    return currentValue;
  };

  const onDebounce = useMemo(
    () =>
      debounce((value: string) => {
        onDebounceChange?.(value);
      }, debounceTime ?? DEBOUNCE_TIME),
    [onDebounceChange, debounceTime]
  );

  useEffect(() => {
    if (!focused) {
      setLastValue(value);
    }
  }, [value, focused]);

  /**
   * Chrome and Edge already filter characters for  <input type="number"> HTML elements.
   * Firefox on the other hand does not do this. To ensure consistent behavior, additional filtering is done for firefox.
   */
  function interceptInput(evt: KeyboardEvent<HTMLDivElement>) {
    if (!isFirefox || props.type !== 'number') {
      return;
    }
    if (!isAllowedKeyForNumberInput(evt.key, evt.ctrlKey, lastValue)) {
      evt.preventDefault();
    }
  }

  return (
    <>
      <TextField
        InputLabelProps={InputLabelProps}
        {...textFieldProps}
        onFocus={handleFocus}
        onBlur={(event) => {
          const currentValue = handleBlur(event);
          if (onUnfocus && ApplicationConfig.immediatelySyncToConference) {
            onUnfocus(currentValue);
          }
        }}
        value={lastValue}
        onChange={handleChange}
        autoComplete='off'
        onKeyDown={interceptInput}
      />
      <SnackbarAlert open={showError} onClose={() => setShowError(false)} message={errorMessage} severity='error' />
    </>
  );
}
